"use client";

import Link from "next/link";
import React, { useEffect } from "react";
import { useTranslations } from "next-intl";
import { trackEducationClaimEvent } from "@/lib/analytics/educationClaim";
import { EDUCATION_DISCOVERY_PROMO_VIDEO } from "@/config/general/promo-videos";

interface ClaimBenefitsMainProps {
  locale: string;
  slug: string;
  returnTo?: string | null;
}

const ClaimBenefitsMain: React.FC<ClaimBenefitsMainProps> = ({ locale, slug, returnTo = null }) => {
  const t = useTranslations("EducationClaim");
  const safeReturnTo = returnTo && returnTo.startsWith("/") ? returnTo : null;
  const profileHref = safeReturnTo
    ? `/${locale}/education/${encodeURIComponent(slug)}?returnTo=${encodeURIComponent(safeReturnTo)}`
    : `/${locale}/education/${encodeURIComponent(slug)}`;
  const claimHref = safeReturnTo
    ? `/${locale}/education/${encodeURIComponent(slug)}/claim?returnTo=${encodeURIComponent(safeReturnTo)}`
    : `/${locale}/education/${encodeURIComponent(slug)}/claim`;

  const benefits = [
    t("ClaimBenefitAccurateInfo"),
    t("ClaimBenefitServices"),
    t("ClaimBenefitDifferent"),
    t("ClaimBenefitDiscovery"),
    t("ClaimBenefitUpToDate"),
    t("ClaimBenefitTrust"),
    t("ClaimBenefitFree"),
  ];

  const faqItems = [
    [t("ClaimBenefitsFaqFreeQuestion"), t("ClaimBenefitsFaqFreeAnswer")],
    [t("ClaimBenefitsFaqSubscriptionQuestion"), t("ClaimBenefitsFaqSubscriptionAnswer")],
    [t("ClaimBenefitsFaqMeaningQuestion"), t("ClaimBenefitsFaqMeaningAnswer")],
    [t("ClaimBenefitsFaqWhoQuestion"), t("ClaimBenefitsFaqWhoAnswer")],
    [t("ClaimBenefitsFaqDirectoriesQuestion"), t("ClaimBenefitsFaqDirectoriesAnswer")],
    [t("ClaimBenefitsFaqMapsQuestion"), t("ClaimBenefitsFaqMapsAnswer")],
    [t("ClaimBenefitsFaqUpdateQuestion"), t("ClaimBenefitsFaqUpdateAnswer")],
    [t("ClaimBenefitsFaqNotClaimQuestion"), t("ClaimBenefitsFaqNotClaimAnswer")],
  ];

  const howItWorksSteps = [
    {
      title: t("ClaimBenefitsHowItWorksStep1Title"),
      body: t("ClaimBenefitsHowItWorksStep1Body"),
    },
    {
      title: t("ClaimBenefitsHowItWorksStep2Title"),
      body: t("ClaimBenefitsHowItWorksStep2Body"),
    },
    {
      title: t("ClaimBenefitsHowItWorksStep3Title"),
      body: t("ClaimBenefitsHowItWorksStep3Body"),
    },
  ];

  useEffect(() => {
    void trackEducationClaimEvent("tc_claim_benefits_viewed", {
      locale,
      slug,
    });
  }, [locale, slug]);

  const trackContinue = () => {
    void trackEducationClaimEvent("tc_claim_benefits_continue_clicked", {
      locale,
      slug,
    });
  };

  return (
    <section className="course__area pt-40 pb-80">
      <div className="classBase container">
        <div className="mb-4">
          <Link href={profileHref} className="btn btn-outline-primary btn-sm">
            {t("ClaimBenefitsBackToProfile")}
          </Link>
        </div>

        <div className="d-flex flex-column gap-4 mb-4">
          <div className="p-4 p-md-5 bg-white border rounded-4" style={{ boxShadow: "0 18px 36px rgba(39, 76, 119, 0.08)" }}>
            <div className="text-uppercase small text-muted fw-semibold mb-2">
              <h1>{t("ClaimBenefitsEyebrow")}</h1>
            </div>
            <p className="text-muted mb-4">{t("ClaimBenefitsHeroLead")}</p>
            <p className="text-muted mb-4">{t("ClaimBenefitsHeroBody")}</p>
            <div className="d-flex gap-2 flex-wrap">
              <Link href={claimHref} className="btn btn-primary" onClick={trackContinue}>
                {t("ClaimBenefitsPrimaryCta")}
              </Link>
              <a href="#claim-benefits-faq" className="btn btn-outline-primary">
                {t("ClaimBenefitsSecondaryCta")}
              </a>
            </div>
            <p className="small text-muted mt-3 mb-0">{t("ClaimBenefitsTrustStrip")}</p>
          </div>

          <div
            className="p-4 border rounded-4"
            style={{
              background: "linear-gradient(180deg, #f3f6fa 0%, #eef2f7 100%)",
              borderColor: "#cfd8e3",
              boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.7)",
            }}
          >
            <h2 className="h4 mb-3">{t("ClaimBenefitsListTitle")}</h2>
            <div className="d-flex flex-column gap-3">
              {benefits.map((benefit) => (
                <div key={benefit} className="d-flex gap-2 align-items-start">
                  <span aria-hidden="true" className="text-success fw-bold">✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 border rounded-4 bg-white" style={{ borderColor: "#dce6f1" }}>
            <h2 className="h4 mb-3">{t("ClaimBenefitsHowItWorksTitle")}</h2>
            <div className="d-flex flex-column gap-3">
              {howItWorksSteps.map((step, index) => (
                <div key={step.title} className="d-flex gap-3 align-items-start">
                  <span
                    aria-hidden="true"
                    className="d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                    style={{ width: 28, height: 28, background: "#1f7a4f", flexShrink: 0 }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="h6 mb-1">{step.title}</h3>
                    <p className="text-muted mb-0">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <section
            role="region"
            aria-label="Claim benefits promo video"
            className="p-3 border rounded-4 bg-white"
            style={{ borderColor: "#dce6f1" }}
          >
            <video
              width="100%"
              height="auto"
              controls
              preload="metadata"
              poster={EDUCATION_DISCOVERY_PROMO_VIDEO.poster}
              playsInline
              style={{ borderRadius: "14px", display: "block" }}
            >
              <source src={EDUCATION_DISCOVERY_PROMO_VIDEO.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </section>
        </div>

        <div id="claim-benefits-faq" className="card" style={{ borderRadius: 16 }}>
          <div className="card-body p-4">
            <h2 className="h4 mb-3">{t("ClaimBenefitsFaqTitle")}</h2>
            <div className="accordion" id="claimBenefitsFaqAccordion">
              {faqItems.map(([question, answer], index) => {
                const collapseId = `claim-benefits-faq-${index}`;
                const headingId = `${collapseId}-heading`;

                return (
                  <div className="accordion-item" key={question}>
                    <h3 className="accordion-header" id={headingId}>
                      <button
                        className={`accordion-button ${index === 0 ? "" : "collapsed"}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#${collapseId}`}
                        aria-expanded={index === 0 ? "true" : "false"}
                        aria-controls={collapseId}
                      >
                        {question}
                      </button>
                    </h3>
                    <div
                      id={collapseId}
                      className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                      aria-labelledby={headingId}
                      data-bs-parent="#claimBenefitsFaqAccordion"
                    >
                      <div className="accordion-body text-muted">{answer}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 d-flex gap-2 flex-wrap">
              <Link href={claimHref} className="btn btn-primary" onClick={trackContinue}>
                {t("ClaimBenefitsPrimaryCta")}
              </Link>
              <Link href={profileHref} className="btn btn-outline-primary">
                {t("ClaimBenefitsBackToProfile")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClaimBenefitsMain;
